import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { Calendar, Clock, Save, Trash2, Plus, Loader2, AlertCircle, ChevronDown, ChevronUp, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface TimeSlot {
  open: string;
  close: string;
}

interface SpecialOpeningHours {
  id?: number;
  date: string;
  isOpen: boolean;
  singleSlot?: TimeSlot;
  lunchSlot?: TimeSlot;
  dinnerSlot?: TimeSlot;
  notes?: string;
}

interface SpecialOpeningHoursManagerProps {
  restaurantId?: string | number;
  menuId?: string | number;
}

const SpecialOpeningHoursManager: React.FC<SpecialOpeningHoursManagerProps> = ({ restaurantId, menuId }) => {
  const { t } = useTranslation();
  const [specialHours, setSpecialHours] = useState<SpecialOpeningHours[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<SpecialOpeningHours>({
    date: '',
    isOpen: true,
    singleSlot: { open: '09:00', close: '18:00' },
    lunchSlot: undefined,
    dinnerSlot: undefined,
    notes: ''
  });
  const [mode, setMode] = useState<'single' | 'split'>('single');
  const [isExpandedList, setIsExpandedList] = useState<number[]>([]);

  useEffect(() => {
    fetchSpecialHours();
  }, [restaurantId, menuId]);

  const fetchSpecialHours = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const id = restaurantId || menuId;
      const response = await axios.get(`/restaurants/${id}/special-opening-hours`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSpecialHours(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch special hours:', error);
      toast.error(t('special_opening_hours.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.date) {
      toast.error(t('special_opening_hours.messages.date_required'));
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const id = restaurantId || menuId;
      
      const dataToSave: any = {
        date: formData.date,
        isOpen: formData.isOpen,
        notes: formData.notes || null
      };

      if (formData.isOpen) {
        if (mode === 'split') {
          dataToSave.lunchSlot = formData.lunchSlot || null;
          dataToSave.dinnerSlot = formData.dinnerSlot || null;
          dataToSave.singleSlot = null;
        } else {
          dataToSave.singleSlot = formData.singleSlot || null;
          dataToSave.lunchSlot = null;
          dataToSave.dinnerSlot = null;
        }
      }

      await axios.post(`/restaurants/${id}/special-opening-hours`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('special_opening_hours.messages.save_success'));
      setShowAddForm(false);
      setEditingIndex(null);
      resetForm();
      fetchSpecialHours();
    } catch (error: any) {
      console.error('Failed to save special hours:', error);
      toast.error(error.response?.data?.error || t('special_opening_hours.messages.save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (specialHoursId: number) => {
    if (!confirm(t('special_opening_hours.delete_confirm'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const id = restaurantId || menuId;
      await axios.delete(`/restaurants/${id}/special-opening-hours/${specialHoursId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('special_opening_hours.messages.delete_success'));
      fetchSpecialHours();
    } catch (error: any) {
      console.error('Failed to delete special hours:', error);
      toast.error(t('special_opening_hours.messages.delete_error'));
    }
  };

  const handleEdit = (index: number) => {
    const item = specialHours[index];
    setFormData({
      date: item.date,
      isOpen: item.isOpen,
      singleSlot: item.singleSlot || { open: '09:00', close: '18:00' },
      lunchSlot: item.lunchSlot,
      dinnerSlot: item.dinnerSlot,
      notes: item.notes || ''
    });
    setMode(item.lunchSlot && item.dinnerSlot ? 'split' : 'single');
    setEditingIndex(index);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      date: '',
      isOpen: true,
      singleSlot: { open: '09:00', close: '18:00' },
      lunchSlot: undefined,
      dinnerSlot: undefined,
      notes: ''
    });
    setMode('single');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateMobile = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const specialDate = new Date(dateString);
    specialDate.setHours(0, 0, 0, 0);
    
    if (specialDate.getTime() === today.getTime()) {
      return t('special_opening_hours.today');
    }
    
    if (specialDate.getTime() === today.getTime() + 86400000) {
      return t('special_opening_hours.tomorrow');
    }
    
    return date.toLocaleDateString('it-IT', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const isExpired = (dateString: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const specialDate = new Date(dateString);
    specialDate.setHours(0, 0, 0, 0);
    return specialDate < today;
  };

  const toggleExpand = (index: number) => {
    if (isExpandedList.includes(index)) {
      setIsExpandedList(isExpandedList.filter(i => i !== index));
    } else {
      setIsExpandedList([...isExpandedList, index]);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 md:w-6 md:h-6" />
            {t('special_opening_hours.title')}
          </h3>
          <p className="text-gray-600 text-sm md:text-base mt-1">
            {t('special_opening_hours.subtitle')}
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowAddForm(true);
            setEditingIndex(null);
          }}
          className="px-3 py-2 md:px-4 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-sm md:text-base w-full sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden xs:inline">{t('special_opening_hours.add_special_hours')}</span>
          <span className="inline xs:hidden">{t('special_opening_hours.add')}</span>
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-base md:text-lg font-semibold text-gray-900">
              {editingIndex !== null ? t('special_opening_hours.edit_special_hours') : t('special_opening_hours.add_special_hours_title')}
            </h4>
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditingIndex(null);
                resetForm();
              }}
              className="md:hidden p-1 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('special_opening_hours.date_required')}
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="flex items-center justify-start md:justify-end pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isOpen}
                  onChange={(e) => setFormData({ ...formData, isOpen: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">{t('special_opening_hours.restaurant_is_open')}</span>
              </label>
            </div>
          </div>

          {formData.isOpen && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('special_opening_hours.schedule_type')}
                </label>
                <div className="flex flex-col xs:flex-row gap-2 md:gap-4">
                  <label className="flex items-center gap-2 cursor-pointer p-2 rounded border border-gray-200 hover:bg-gray-50">
                    <input
                      type="radio"
                      value="single"
                      checked={mode === 'single'}
                      onChange={(e) => setMode(e.target.value as 'single')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">{t('special_opening_hours.single_slot')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer p-2 rounded border border-gray-200 hover:bg-gray-50">
                    <input
                      type="radio"
                      value="split"
                      checked={mode === 'split'}
                      onChange={(e) => setMode(e.target.value as 'split')}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm">{t('special_opening_hours.split_lunch_dinner')}</span>
                  </label>
                </div>
              </div>

              {mode === 'single' ? (
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('special_opening_hours.open_time')}
                    </label>
                    <input
                      type="time"
                      value={formData.singleSlot?.open || '09:00'}
                      onChange={(e) => setFormData({
                        ...formData,
                        singleSlot: { ...formData.singleSlot!, open: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('special_opening_hours.close_time')}
                    </label>
                    <input
                      type="time"
                      value={formData.singleSlot?.close || '18:00'}
                      onChange={(e) => setFormData({
                        ...formData,
                        singleSlot: { ...formData.singleSlot!, close: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('special_opening_hours.lunch_slot')}
                    </label>
                    <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 md:gap-4">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Open</label>
                        <input
                          type="time"
                          value={formData.lunchSlot?.open || '12:00'}
                          onChange={(e) => setFormData({
                            ...formData,
                            lunchSlot: { ...formData.lunchSlot || { open: '12:00', close: '15:00' }, open: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Close</label>
                        <input
                          type="time"
                          value={formData.lunchSlot?.close || '15:00'}
                          onChange={(e) => setFormData({
                            ...formData,
                            lunchSlot: { ...formData.lunchSlot || { open: '12:00', close: '15:00' }, close: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('special_opening_hours.dinner_slot')}
                    </label>
                    <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 md:gap-4">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Open</label>
                        <input
                          type="time"
                          value={formData.dinnerSlot?.open || '19:00'}
                          onChange={(e) => setFormData({
                            ...formData,
                            dinnerSlot: { ...formData.dinnerSlot || { open: '19:00', close: '23:00' }, open: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Close</label>
                        <input
                          type="time"
                          value={formData.dinnerSlot?.close || '23:00'}
                          onChange={(e) => setFormData({
                            ...formData,
                            dinnerSlot: { ...formData.dinnerSlot || { open: '19:00', close: '23:00' }, close: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('special_opening_hours.notes')}
            </label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm md:text-base"
              rows={2}
              placeholder={t('special_opening_hours.notes_placeholder')}
            />
          </div>

          <div className="flex flex-col xs:flex-row gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm md:text-base order-2 xs:order-1"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {t('special_opening_hours.save')}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditingIndex(null);
                resetForm();
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm md:text-base order-1 xs:order-2"
            >
              {t('special_opening_hours.cancel')}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3 md:space-y-4">
        {specialHours.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 md:p-8 text-center">
            <Calendar className="w-10 h-10 md:w-12 md:h-12 text-[#7BD5B5]/40 mx-auto mb-3 md:mb-4" />
            <p className="text-gray-600 text-sm md:text-base">{t('special_opening_hours.no_special_hours')}</p>
            <p className="text-xs md:text-sm text-gray-500 mt-2">
              {t('special_opening_hours.no_special_hours_description')}
            </p>
          </div>
        ) : (
          specialHours.map((item, index) => {
            const isExpanded = isExpandedList.includes(index);
            const isItemExpired = isExpired(item.date);
            
            return (
              <div
                key={item.id || index}
                className={`bg-white border rounded-lg p-3 md:p-4 ${
                  isItemExpired ? 'border-gray-300 opacity-60' : 'border-gray-200'
                }`}
              >
                {/* Mobile Compact View */}
                <div className="md:hidden">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => toggleExpand(index)}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Clock className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <h4 className="font-medium text-gray-900 truncate text-sm">
                          {formatDateMobile(item.date)}
                        </h4>
                        <div className="text-xs text-gray-500">
                          {item.isOpen ? (
                            item.singleSlot ? (
                              <span>{item.singleSlot.open} - {item.singleSlot.close}</span>
                            ) : (
                              <span>{t('special_opening_hours.split_hours')}</span>
                            )
                          ) : (
                            <span className="text-red-600 font-medium">{t('special_opening_hours.closed')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      {isItemExpired && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                          {t('special_opening_hours.expired')}
                        </span>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content for Mobile */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 mb-1">
                          {formatDate(item.date)}
                        </h5>
                        {item.isOpen ? (
                          <div className="space-y-1 text-sm text-gray-600">
                            {item.singleSlot && (
                              <p>
                                <span className="font-medium">{t('special_opening_hours.hours')}</span>{' '}
                                {item.singleSlot.open} - {item.singleSlot.close}
                              </p>
                            )}
                            {item.lunchSlot && item.dinnerSlot && (
                              <>
                                <p>
                                  <span className="font-medium">{t('special_opening_hours.lunch')}</span>{' '}
                                  {item.lunchSlot.open} - {item.lunchSlot.close}
                                </p>
                                <p>
                                  <span className="font-medium">{t('special_opening_hours.dinner')}</span>{' '}
                                  {item.dinnerSlot.open} - {item.dinnerSlot.close}
                                </p>
                              </>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-red-600 font-medium">{t('special_opening_hours.closed')}</p>
                        )}
                      </div>
                      
                      {item.notes && (
                        <div>
                          <p className="text-xs text-gray-500 italic">{item.notes}</p>
                        </div>
                      )}
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(index)}
                          className="px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg text-sm flex items-center gap-1 border border-blue-200"
                        >
                          <Save className="w-3 h-3" />
                          {t('special_opening_hours.edit')}
                        </button>
                        <button
                          onClick={() => item.id && handleDelete(item.id)}
                          className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm flex items-center gap-1 border border-red-200"
                        >
                          <Trash2 className="w-3 h-3" />
                          {t('special_opening_hours.delete')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Desktop View */}
                <div className="hidden md:flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-gray-900">
                        {formatDate(item.date)}
                      </h4>
                      {isItemExpired && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                          {t('special_opening_hours.expired')}
                        </span>
                      )}
                    </div>

                    {item.isOpen ? (
                      <div className="space-y-1 text-sm text-gray-600">
                        {item.singleSlot && (
                          <p>
                            <span className="font-medium">{t('special_opening_hours.hours')}</span>{' '}
                            {item.singleSlot.open} - {item.singleSlot.close}
                          </p>
                        )}
                        {item.lunchSlot && item.dinnerSlot && (
                          <>
                            <p>
                              <span className="font-medium">{t('special_opening_hours.lunch')}</span>{' '}
                              {item.lunchSlot.open} - {item.lunchSlot.close}
                            </p>
                            <p>
                              <span className="font-medium">{t('special_opening_hours.dinner')}</span>{' '}
                              {item.dinnerSlot.open} - {item.dinnerSlot.close}
                            </p>
                          </>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-red-600 font-medium">{t('special_opening_hours.closed')}</p>
                    )}

                    {item.notes && (
                      <p className="text-sm text-gray-500 mt-2 italic">{item.notes}</p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(index)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Edit"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => item.id && handleDelete(item.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SpecialOpeningHoursManager;